/**
 * Step 4: confirmation summary. Collects couple names and event date,
 * then shows a preview of what will be created before the coordinator
 * commits.
 *
 * Three summary variants:
 *   - Blank:     short description of the empty room
 *   - Ceremony:  chair count, aisle description, arbor status
 *   - Reception: projected table/chair/seat counts (unchanged from original)
 *
 * Couple names + event date are collected here rather than earlier,
 * since they don't affect the generation math and asking for them last
 * keeps the early steps focused purely on the layout decision.
 */

import { buildSeatingPlan } from '../../domain/generation/seatingMath';
import { decideDanceFloorSize } from '../../domain/danceFloor';
import { useRoomDefinitions } from '../../api/roomDefinitions';
import type { WizardData } from './NewWeddingWizard';

interface StepConfirmGenerateProps {
  data: WizardData;
  onChangeCoupleNames: (value: string) => void;
  onChangeEventDate: (value: string) => void;
  onConfirm: () => void;
  submitting: boolean;
  error: string | null;
}

const STYLE_LABELS: Record<string, string> = {
  classic_banquet: 'Classic Banquet',
  long_table_feast: 'Long Table Feast',
  mixed_modern: 'Mixed Modern',
  lounge_cocktail: 'Lounge & Cocktail',
};

export function StepConfirmGenerate({
  data,
  onChangeCoupleNames,
  onChangeEventDate,
  onConfirm,
  submitting,
  error,
}: StepConfirmGenerateProps) {
  const { data: rooms } = useRoomDefinitions();
  const room = rooms?.find((r) => r.room === data.room);

  if (!data.room || (!data.layoutStyle && !data.startBlank && !data.startCeremony)) {
    return (
      <p className="font-body text-sm text-danger">
        {'Missing required selections \u2014 go back and complete every step.'}
      </p>
    );
  }

  // Reception projected counts (only computed for reception path)
  const seatingPlan =
    !data.startBlank && !data.startCeremony && data.layoutStyle
      ? buildSeatingPlan(data.guestCount, data.headTableType, data.headTablePartySize, data.layoutStyle)
      : null;

  const danceFloor =
    room && !data.startBlank && !data.startCeremony && data.layoutStyle
      ? decideDanceFloorSize(data.guestCount, room)
      : null;

  const headTableSeats =
    data.headTableType === 'sweetheart_table' ? 2 : data.headTablePartySize ?? 6;

  const totalProjectedSeats = !seatingPlan
    ? 0
    : data.layoutStyle === 'lounge_cocktail'
      ? seatingPlan.loungeSeatedGuests + seatingPlan.loungeStandingGuests + headTableSeats
      : seatingPlan.roundTableCount * 8 + seatingPlan.rectTable8Count * 8 + headTableSeats;

  // Ceremony row estimation for the summary box
  // This is a rough preview — the exact number may differ slightly from
  // what generateCeremonyLayout() produces when chairCount doesn't divide
  // evenly into the chosen rows. We show what the coordinator asked for
  // rather than the algorithmic breakdown, since the algorithm details
  // don't matter at confirmation time.
  const ceremonyRows =
    data.startCeremony && data.ceremonyChairCount > 0
      ? Math.ceil(Math.ceil(data.ceremonyChairCount / 2) / 6) // rough: 6 chairs per side per row
      : 0;

  const canSubmit = data.coupleNames.trim().length > 0 && data.eventDate.length > 0 && !submitting;

  const headingText = data.startBlank
    ? "Just a couple details, then it\u2019s yours to build"
    : data.startCeremony
      ? "Just a couple details, then we\u2019ll lay out the ceremony"
      : "Just a couple details, then let\u2019s build it";

  const confirmButtonText = submitting
    ? 'Creating\u2026'
    : data.startBlank
      ? '\u2728 Create Blank Layout'
      : data.startCeremony
        ? '\u2728 Generate Ceremony Layout'
        : '\u2728 Generate My Layout';

  return (
    <div>
      <h2 className="text-center font-display text-2xl text-ink-700">{headingText}</h2>

      <div className="mt-6 space-y-3">
        <label className="block">
          <span className="font-body text-sm font-medium text-ink-600">Couple names</span>
          <input
            type="text"
            value={data.coupleNames}
            onChange={(e) => onChangeCoupleNames(e.target.value)}
            placeholder="Smith & Lee"
            className="mt-1 w-full rounded border border-ivory-300 bg-white px-3 py-2 font-body text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200"
          />
        </label>

        <label className="block">
          <span className="font-body text-sm font-medium text-ink-600">Event date</span>
          <input
            type="date"
            value={data.eventDate}
            onChange={(e) => onChangeEventDate(e.target.value)}
            className="mt-1 w-full rounded border border-ivory-300 bg-white px-3 py-2 font-body text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200"
          />
        </label>
      </div>

      {/* ——— BLANK summary ——— */}
      {data.startBlank && (
        <div className="mt-6 rounded-lg border border-ivory-300 bg-white p-5">
          <p className="font-body text-xs uppercase tracking-wide text-ink-400">Starting point</p>
          <p className="mt-2 font-body text-sm text-ink-600">
            An empty {room?.displayName ?? 'room'}, ready for you to add tables, the dance floor,
            and everything else exactly where you want them.
          </p>
          <p className="mt-3 font-body text-xs text-ink-400">{data.guestCount} guests expected</p>
        </div>
      )}

      {/* ——— CEREMONY summary ——— */}
      {data.startCeremony && (
        <div className="mt-6 rounded-lg border border-ivory-300 bg-white p-5">
          <p className="font-body text-xs uppercase tracking-wide text-ink-400">
            Here&rsquo;s what we&rsquo;ll generate for you
          </p>
          <ul className="mt-3 space-y-1.5 font-body text-sm text-ink-600">
            <li>
              {'\u2713'} {data.ceremonyChairCount} chairs in {ceremonyRows} row{ceremonyRows !== 1 ? 's' : ''}
            </li>
            <li>{'\u2713'} Center aisle dividing left and right seating sections</li>
            {data.ceremonyIncludeArbor && (
              <li>{'\u2713'} Arbor at the ceremony focal end (moveable)</li>
            )}
          </ul>
          <p className="mt-4 font-body text-xs text-ink-400">
            {room?.displayName ?? 'Room'} \u00b7 All chairs and the arbor are individually
            moveable and deletable after generation.
          </p>
        </div>
      )}

      {/* ——— RECEPTION summary ——— */}
      {!data.startBlank && !data.startCeremony && (
        <div className="mt-6 rounded-lg border border-ivory-300 bg-white p-5">
          <p className="font-body text-xs uppercase tracking-wide text-ink-400">
            Here&rsquo;s what we&rsquo;ll generate for you
          </p>

          <ul className="mt-3 space-y-1.5 font-body text-sm text-ink-600">
            {seatingPlan && seatingPlan.roundTableCount > 0 && (
              <li>{'\u2713'} {seatingPlan.roundTableCount} round tables ({seatingPlan.roundTableCount * 8} seats)</li>
            )}
            {seatingPlan && seatingPlan.rectTable8Count > 0 && (
              <li>
                {'\u2713'} {seatingPlan.rectTable8Count} rectangle tables ({seatingPlan.rectTable8Count * 8} seats)
              </li>
            )}
            {seatingPlan && seatingPlan.cocktailTableCount > 0 && (
              <li>{'\u2713'} {seatingPlan.cocktailTableCount} cocktail tables</li>
            )}
            <li>
              {'\u2713'}{' '}
              {data.headTableType === 'sweetheart_table'
                ? 'Sweetheart table (2 seats)'
                : `Head table (${headTableSeats} seats)`}
            </li>
            {danceFloor && (
              <li>
                {'\u2713'}{' '}
                {danceFloor.size === 'dance_floor_18' ? "18\u2032 \u00d7 18\u2032" : "15\u2032 \u00d7 15\u2032"}{' '}
                dance floor
              </li>
            )}
            <li>{'\u2713'} DJ booth, cake table, gift table, dessert station, photo booth</li>
          </ul>

          <p
            className={
              totalProjectedSeats === data.guestCount
                ? 'mt-4 font-display text-base text-evergreen-600'
                : 'mt-4 font-display text-base text-warning'
            }
          >
            {totalProjectedSeats} / {data.guestCount}{' '}
            {data.layoutStyle === 'lounge_cocktail' ? 'guests accounted for' : 'guests seated'}{' '}
            {totalProjectedSeats === data.guestCount ? '\u2713' : '\u26A0'}
          </p>

          <p className="mt-1 font-body text-xs text-ink-400">
            {data.layoutStyle ? STYLE_LABELS[data.layoutStyle] : ''} style
          </p>
        </div>
      )}

      <button
        onClick={onConfirm}
        disabled={!canSubmit}
        className="mt-6 w-full rounded bg-evergreen-600 px-4 py-3 font-body font-medium text-ivory-50 hover:bg-evergreen-700 disabled:opacity-40"
      >
        {confirmButtonText}
      </button>

      {error && (
        <p className="mt-2 text-center font-body text-xs text-danger">{error}</p>
      )}

      {!canSubmit && !submitting && !error && (
        <p className="mt-2 text-center font-body text-xs text-ink-400">
          {!data.coupleNames.trim() && !data.eventDate
            ? "Add the couple's names and event date above to continue."
            : !data.coupleNames.trim()
              ? "Add the couple's names above to continue."
              : 'Add the event date above to continue.'}
        </p>
      )}
    </div>
  );
}
